import {
  readFeatureCookieByKey,
  setFeatureCookieByKey,
} from '@components/common';
import { _CONSTANTS } from '@components/common/constant';
import { getExperimentVariant } from 'helper/experiments/experimentActions';
import { RMCPortalExperiments } from 'helper/experiments/experimentConstants';

/**
 * Processes job details based on the free jobs experiment and user's viewing history.
 *
 * @param {any} jobDetailResp - The job details response object to process
 * @param {string} jobID - The ID of the job being viewed
 * @param {string} deviceType - Device type ('mobile' or 'desktop')
 * @param {object} categoryDetails - Details about the category being viewed
 * @param {boolean} isPremiumUser - Whether the current user is a premium user
 * @returns {any} The processed job details response
 */
export const processFreeJobDetailsCategory = (
  jobDetailResp: any,
  jobID: string,
  deviceType: string,
  categoryDetails: any,
  isPremiumUser: boolean
): any => {
  // Skip processing for premium users
  if (isPremiumUser || !jobDetailResp.data.isFreeJob) {
    return jobDetailResp;
  }

  // Process only if experiment is active (variant > 2)
  const variantFreeJobs = getExperimentVariant(
    deviceType === 'mobile'
      ? RMCPortalExperiments.rmcFreeJobsV2Mobile.id
      : RMCPortalExperiments.rmcFreeJobsV2Desktop.id
  );
  if (variantFreeJobs <= 2) {
    return jobDetailResp;
  }

  // Get category viewed jobs from cookie
  const categoryId = categoryDetails.id;
  const cookieDetails = readFeatureCookieByKey(_CONSTANTS.FREE_JOBS_VIEWED);
  const viewedJobs =
    cookieDetails && Array.isArray(cookieDetails[categoryId])
      ? cookieDetails[categoryId]
      : [];

  // Process based on viewing history
  if (viewedJobs.length < 2) {
    // trackNewJobView(jobID, categoryId, viewedJobs);
    if (!viewedJobs.includes(jobID)) {
      setFeatureCookieByKey({
        key: _CONSTANTS.FREE_JOBS_VIEWED,
        value: JSON.stringify({
          ...cookieDetails,
          [categoryId]: [...viewedJobs, jobID],
        }),
      });
    }
  } else if (!viewedJobs.includes(jobID)) {
    jobDetailResp.data.isFreeJob = false;
    jobDetailResp.data.description = null;
  }
  return jobDetailResp;
};

/**
 * Process job details based on experiment variant and viewing history
 *
 * This function implements the free job quota system for non-premium users.
 * It allows viewing a limited number of jobs per category, and nullifies
 * sensitive fields when the quota is exceeded.
 *
 * @param {any} jobDetails - The job details to potentially modify
 * @param {string} deviceType - 'mobile' or 'desktop'
 * @param {string} cookieHeader - Cookie header for server-side operations
 * @param {boolean} isPremiumUser - Whether the user is a premium user
 * @param {any} [res] - Response object for setting cookies
 * @returns {any} The original or modified job details
 */
export const processJobDetailsForExperiment = (
  jobDetails: any,
  deviceType: string,
  cookieHeader: string = '',
  isPremiumUser: boolean,
  res?: any
): any => {
  // Premium users always get full access
  if (isPremiumUser || !jobDetails || !jobDetails.isFreeJob) {
    return jobDetails;
  }

  try {
    // Step 1: Get appropriate experiment based on device type
    const experimentId =
      deviceType === 'mobile'
        ? RMCPortalExperiments.rmcFreeJobsV2Mobile.id
        : RMCPortalExperiments.rmcFreeJobsV2Desktop.id;
    const experimentVariant = getExperimentVariant(experimentId, cookieHeader);
    // Only apply free job quota system for variant > 2
    if (!experimentVariant || experimentVariant <= 2) {
      return jobDetails;
    }

    // Step 2: Check if job has been viewed before (never nullify previously viewed jobs)
    const viewHistory = getJobViewHistory(cookieHeader);
    if (hasJobBeenViewed(jobDetails.id, viewHistory)) {
      return jobDetails;
    }

    // Step 3: Process job categories
    const categoryIds = jobDetails.cachedInCategory;
    if (!Array.isArray(categoryIds) || categoryIds.length === 0) {
      return jobDetails;
    }

    // Step 4: Process viewing history and update cookies
    return processJobViewingHistory(jobDetails, categoryIds, cookieHeader, res);
  } catch (error) {
    console.error('Error in processJobDetailsForExperiment:', error);
    return jobDetails; // Return unmodified job details on error
  }
};
/**
 * Get job viewing history from cookies
 */
const getJobViewHistory = (cookieHeader: string) => {
  const viewHistory =
    readFeatureCookieByKey(_CONSTANTS.FREE_JOBS_VIEWED, cookieHeader) || {};
  return typeof viewHistory === 'object' ? viewHistory : {};
};

/**
 * Process job viewing history and update cookies
 *
 * This function manages the job quota system by:
 * 1. Checking if we've seen this job before
 * 2. Attempting to add this job to relevant categories if within quota
 * 3. Nullifying sensitive fields if quota is exceeded
 *
 * @param {any} jobDetails - The job details to potentially modify
 * @param {string[]} categoryIds - Categories the job belongs to
 * @param {string} cookieHeader - Cookie header for server-side operations
 * @param {any} [res] - Response object for setting cookies
 * @returns {any} The original or modified job details
 */
const processJobViewingHistory = (
  jobDetails: any,
  categoryIds: string[],
  cookieHeader: string,
  res?: any
): any => {
  // Get view history from cookies (safety check, though we already checked above)
  let viewHistory = getJobViewHistory(cookieHeader);

  // Try to add job to categories that have available quota
  const { updated, foundSpace } = addJobToCategories(
    jobDetails.id,
    categoryIds,
    viewHistory
  );

  // Save updated cookie if the view history was modified
  if (updated) {
    setFeatureCookieByKey({
      key: _CONSTANTS.FREE_JOBS_VIEWED,
      value: JSON.stringify(viewHistory),
      cookieHeader: cookieHeader,
      res,
    });
  }

  // If job couldn't be added to any category (quota exceeded), nullify sensitive fields
  if (!foundSpace) {
    return {
      ...jobDetails,
      isFreeJob: false,
      description: null,
    };
  }

  return jobDetails;
};

/**
 * Check if a job has been viewed in any category
 *
 * Searches through the entire view history to see if this job ID appears
 * in any category, not just the ones this job belongs to.
 *
 * @param {string} jobId - The ID of the job to check
 * @param {Record<string, string[]>} viewHistory - The view history data from cookies
 * @returns {boolean} True if the job has been viewed in any category
 */
const hasJobBeenViewed = (
  jobId: string,
  viewHistory: Record<string, string[]>
): boolean => {
  // Early return for empty/invalid history
  if (!viewHistory || typeof viewHistory !== 'object') {
    return false;
  }

  // Check all categories in the view history
  for (const categoryId in viewHistory) {
    // Make sure the category entry is a valid array
    if (
      Array.isArray(viewHistory[categoryId]) &&
      viewHistory[categoryId].includes(jobId)
    ) {
      return true;
    }
  }

  return false;
};

/**
 * Try to add job to eligible categories within quota limits
 *
 * This function implements the core quota logic:
 * 1. First checks if the job has been seen before (safety check)
 * 2. Then checks if any category has reached its quota (2 jobs)
 * 3. If quota available, adds the job to all applicable categories
 *
 * @param {string} jobId - The ID of the job to add
 * @param {string[]} categoryIds - Array of category IDs this job belongs to
 * @param {Record<string, string[]>} viewHistory - The view history data from cookies
 * @returns {object} Result with updated and foundSpace flags
 */
const addJobToCategories = (
  jobId: string,
  categoryIds: string[],
  viewHistory: Record<string, string[]>
): { updated: boolean; foundSpace: boolean } => {
  // Initialize tracking variables
  let updated = false;
  let foundSpace = false;

  // Safety check: Never nullify if job exists in any category
  if (hasJobBeenViewed(jobId, viewHistory)) {
    return { updated: false, foundSpace: true };
  }

  // First pass: Check if ANY category is at quota limit
  const hasFullCategory = categoryIds.some(
    (categoryId) =>
      viewHistory[categoryId] &&
      Array.isArray(viewHistory[categoryId]) &&
      viewHistory[categoryId].length >= 2
  );

  // If any category is full, don't add job to any category
  if (hasFullCategory) {
    return { updated: false, foundSpace: false };
  }

  // Second pass: Add job to all applicable categories
  for (const categoryId of categoryIds) {
    // Initialize category if it doesn't exist
    if (!viewHistory[categoryId]) {
      viewHistory[categoryId] = [jobId];
      updated = true;
      foundSpace = true;
      continue;
    }

    // Add to existing category if not already there
    if (
      Array.isArray(viewHistory[categoryId]) &&
      !viewHistory[categoryId].includes(jobId)
    ) {
      viewHistory[categoryId].push(jobId);
      updated = true;
      foundSpace = true;
    }
  }

  return { updated, foundSpace };
};
